// Central registry of SABER table column tooltips (WEB-6569).
//
// One place to review and edit the hover-help shown on every column header.
// Keyed by a table id -> the table's column `key` -> the description string.
// Table headers look these up via `columnHelp(tableId, columnKey)`:
//   - SaberTable: pass `helpKey="<tableId>"`; it titles each <th> by column key.
//   - Grouped tables (TenantAllocTable / GroupedVarianceTable): the SortHeaderButton
//     headers pass `title={columnHelp('<tableId>', c.key)}`.
//
// To add a table: add a new top-level entry whose inner keys match that table's
// column `key`s. Missing entries simply render no tooltip.
export const COLUMN_HELP: Record<string, Record<string, string>> = {
  // ── Shadow Billing › Tenant Allocation & TOU Value ────────────────────────
  tenantAlloc: {
    tenant: 'The tenant receiving a solar allocation at this property.',
    meter: "The tenant's meter / service agreement. A tenant may have more than one.",
    alloc: "The tenant's share of the property's solar generation, as a percent.",
    allocKwh: 'Solar kWh allocated to the tenant over the selected period.',
    gross: 'Gross solar revenue (before the corp discount) attributed to the tenant.',
    net: 'Net solar revenue (after the corp discount) attributed to the tenant.',
    over: "kWh allocated beyond the tenant's consumption (over-allocation), if any."
  },

  // ── Shadow Billing › TOU Rate Breakdown ───────────────────────────────────
  tou: {
    season: 'Utility rate season (e.g. Summer / Winter) for this bucket.',
    tou: 'Time-of-use period (e.g. Peak / Off-Peak) for this bucket.',
    hours: 'Clock hours when this TOU period applies for the tariff and season (from the utility TOU schedule).',
    tariff: 'Utility tariff schedule the rate is drawn from.',
    kwh: 'Allocated solar kWh that fell into this season / TOU bucket.',
    pct: 'This bucket’s allocated kWh as a percent of the table’s total allocated kWh.',
    rate: 'Effective $/kWh rate applied to this bucket (gross $ ÷ allocated kWh).',
    gross: 'Gross solar revenue from this season / TOU bucket.'
  },

  // ── Shadow Billing › Completeness Check ────────────────────────────────────
  completeness: {
    meter: 'The meter / service agreement being checked for billing completeness.',
    tenant: 'Tenant associated with the meter (blank for common-area meters).',
    alloc: 'Solar kWh allocated to this meter over the period.',
    billed: 'kWh actually billed for this meter over the period.',
    unbilled: 'Allocated kWh not yet billed (allocated minus billed).',
    unbilledValue: 'Net solar $ shadow-billed but not yet invoiced for this meter.',
    pct: 'Billed ÷ allocated, as a percent. Low values flag missing billing.',
    status: 'OK / WARN / CRITICAL derived from the completeness percent.'
  },

  // ── Shadow Billing › Variance — Invoices ───────────────────────────────────
  variance: {
    tenant: 'Tenant whose invoice(s) are being compared to the SABER shadow bill.',
    invoice: 'Invoice number and billing window compared against SABER.',
    invoiceAmt: 'Actual gross solar revenue the utility billed on the invoice (before the corp discount) — the basis SABER is compared against.',
    shadowAmt: "SABER's shadow-billed gross solar revenue for the same window. Total Variance = Shadow − Invoice.",
    variance: "Dollar difference between SABER's shadow bill and the utility invoice (Shadow − Invoice). Positive means SABER modeled more than was billed.",
    pct: 'Variance as a percent of the utility-billed amount (the worst invoice in a tenant group).',
    driver: 'What primarily drives the variance (kWh, rate, allocation, discount, or a mix). A colored pill marks a material variance worth reviewing; a muted "Immaterial" pill is below the $1 threshold. Hover a pill for the full explanation.'
  },

  // ── Base Case › Programs (WEB-6559) ────────────────────────────────────────
  basecasePrograms: {
    name: 'Program name (synced from Salesforce / servicing).',
    type: 'Program type: NEM2V, BEHIND_THE_METER, or PASSTHRU.',
    nem: 'NEM tariff version the program operates under.',
    capacity: 'System nameplate capacity in kW (from Salesforce kW_Capacity__c).',
    billingOn: 'Whether billing is currently turned on for the program.',
    billingOnDate: 'Date billing was turned on for the program.',
    ptoDate: 'Permission-to-Operate date — when the solar system went live.'
  },

  // ── Dashboard › Portfolio properties ───────────────────────────────────────
  dashboard: {
    property: 'Property (site) name. Click a row to open its Shadow Billing detail.',
    location: 'City / region of the property.',
    utility: 'Utility serving the property (e.g. PG&E, SCE, SDG&E).',
    investor: 'Investment entity that owns / finances the solar system.',
    capacity: 'System nameplate capacity in kW.',
    billed: 'Solar kWh billed over the selected period.',
    unbilled: 'Allocated kWh not yet billed (shadow-billed but not invoiced).',
    pctOfForecast: 'Billed kWh as a percent of forecasted generation.',
    status: 'Operating or Onboarding status of the property.'
  },

  // ── Health / Config › Data-source freshness ────────────────────────────────
  freshness: {
    property: 'Property the data source feeds.',
    source: 'Data source type (e.g. utility bill, NGOM/inverter, interval data).',
    maxDate: 'Most recent data date available from this source.',
    age: 'Days since the source last refreshed; higher = staler.',
    status: 'FRESH / STALE / MISSING based on the data age.'
  },

  // ── Health / Config › Alerts ─────────────────────────────────────────────────
  alerts: {
    severity: 'Alert severity — HIGH / MEDIUM / LOW.',
    category: 'What the alert is about (e.g. data freshness, billing lag, pipeline).',
    title: 'Short description of the alert.',
    created: 'When the alert was raised.',
    ack: 'Whether the alert has been acknowledged.'
  },

  // ── Engine Status › Up next (run queue) ─────────────────────────────────────
  upNext: {
    id: 'Run-request id.',
    type: 'Run type — INCREMENTAL / LOOKBACK / TARGETED.',
    params: 'Scope / parameters the run will execute with.',
    eligible: 'When the request becomes eligible to run.',
    requestedBy: 'User or system that requested the run.'
  },

  // ── Engine Status › Run history ─────────────────────────────────────────────
  runHistory: {
    id: 'Run-request id.',
    type: 'Run type — INCREMENTAL / LOOKBACK / TARGETED.',
    status: 'QUEUED / CLAIMED / SUCCEEDED / FAILED / CANCELLED.',
    work: 'Work done by the run (program-months processed, failures).',
    duration: 'How long the run took.',
    requestedBy: 'User or system that requested the run.',
    finished: 'When the run finished.'
  },

  // ── Engine Status › Schedules ───────────────────────────────────────────────
  schedules: {
    name: 'Schedule name.',
    type: 'Run type the schedule triggers.',
    cadence: 'Recurrence (cron expression / cadence).',
    nextFire: 'When the schedule next triggers.',
    enabled: 'Whether the schedule is active or paused.',
    priority: 'Queue priority (higher runs sooner).'
  },

  // ── Base Case › Pro-Forma by contract year ──────────────────────────────────
  proForma: {
    year: 'Contract year (Year 1 = first year after PTO).',
    modeledKwh: 'Forecasted (base-case) generation for the year, in kWh.',
    actualKwh: 'Measured generation for the year, in kWh.',
    deltaKwh: 'Actual minus modeled kWh.',
    modeledRevenue: 'Forecasted solar revenue for the year.',
    actualRevenue: 'Actual solar revenue realized for the year.',
    performance: 'Actual ÷ modeled revenue, as a percent.',
    status: 'FORECAST / COMPLETE / PARTIAL for the year.'
  }
}

/** Tooltip text for a column header, or undefined when no help is registered. */
export const columnHelp = (table: string, column: string): string | undefined => COLUMN_HELP[table]?.[column]
